import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import jsPDF from 'jspdf';
import { viewUrl } from '../../services/s3';

// Helper: Convert Image URL to Base64
const getBase64ImageFromUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    try {
        const res = await fetch(imageUrl);
        if (!res.ok) {
            console.warn(`Failed to fetch image: ${imageUrl}, Status: ${res.status}`);
            return null;
        }
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) {
            console.warn(`Fetched content is not an image: ${blob.type}`);
            return null;
        }
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.warn('Image loading failed', error);
        return null;
    }
};

const PDFDownloadFunctionality = ({ open, onClose, associate }) => {
    const [companyData, setCompanyData] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [error, setError] = useState('');

    // Colors matched to the reference image
    const TOP_BOTTOM_STRIP_COLOR = '#2596be'; // The main blue
    const DESIGNATION_BG_COLOR = '#dbeafe';   // Very light blue for designation strip
    const TEXT_COLOR_MAIN = '#000000';
    const TEXT_COLOR_NAME = '#2596be';        // Blue for Name
    const TEXT_COLOR_ID = '#666666';          // Gray for ID Label

    useEffect(() => {
        if (open) {
            fetchCompanyDetails();
        }
    }, [open]);

    const fetchCompanyDetails = async () => {
        setLoadingData(true);
        setError('');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/company/details`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const result = await response.json();

            if (result.success && result.data) {
                setCompanyData(result.data);
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (err) {
            console.error('Failed to fetch company details', err);
            setError('Could not load company details.');
        } finally {
            setLoadingData(false);
        }
    };

    const generateIDCard = async () => {
        if (!companyData || !associate) return;
        setGeneratingPdf(true);

        try {
            // 1. Setup Document (Portrait, ID Card Size ~ 60mm x 96mm)
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [60, 96]
            });

            const cardWidth = 60;
            const cardHeight = 96;
            const centerX = cardWidth / 2;
            const safeWidth = 54; // 3mm margin on each side

            // 2. Preload Images
            const signedLogoUrl = companyData.logo ? await viewUrl(companyData.logo) : null;
            const signedUserPhotoUrl = associate.image ? await viewUrl(associate.image) : null;

            const logoBase64 = signedLogoUrl ? await getBase64ImageFromUrl(signedLogoUrl) : null;
            const userPhotoBase64 = signedUserPhotoUrl ? await getBase64ImageFromUrl(signedUserPhotoUrl) : null;

            // --- BACKGROUND & BORDERS ---
            doc.setFillColor(255, 255, 255);
            doc.rect(0, 0, cardWidth, cardHeight, 'F');

            // Top Strip
            doc.setFillColor(TOP_BOTTOM_STRIP_COLOR);
            doc.rect(0, 0, cardWidth, 4, 'F'); // 4mm height

            // Bottom Strip
            doc.rect(0, cardHeight - 4, cardWidth, 4, 'F'); // 4mm height

            let cursorY = 10; // Start tracking vertical position

                        // --- 1. LOGO SECTION (Centered) ---
            if (logoBase64) {
                const logoW = 20;
                const logoH = 10;
                // Detect format
                const logoFormat = logoBase64.includes('image/png') ? 'PNG' : 'JPEG';
                // Center align logo: (TotalWidth / 2) - (ImageWidth / 2)
                doc.addImage(logoBase64, logoFormat, centerX - (logoW / 2), cursorY - 4, logoW, logoH);
                cursorY += 8; // Move down
            } else {
                // Fallback text if no logo
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.setTextColor(TEXT_COLOR_MAIN);
                const companyName = (companyData.name || 'Company Name').toUpperCase();
                const splitCompanyName = doc.splitTextToSize(companyName, safeWidth);
                doc.text(splitCompanyName, centerX, cursorY, { align: 'center' });
                cursorY += (splitCompanyName.length * 5);
            }

            // --- 2. PHOTO SECTION (Oval Mask) ---
            const photoW = 22;
            const photoH = 22; // Making it square for circle/oval mask logic

            cursorY += 2; // Spacer

            if (userPhotoBase64) {
                try {
                    // Create Oval/Circle Clipping Mask
                    doc.context2d.save();
                    doc.context2d.beginPath();
                    // Use arc instead of ellipse for circular crop (centerX, centerY, radius, startAngle, endAngle)
                    doc.context2d.arc(centerX, cursorY + (photoH / 2), photoW / 2, 0, 2 * Math.PI);
                    doc.context2d.clip();
                    
                    const photoFormat = userPhotoBase64.includes('image/png') ? 'PNG' : 'JPEG';
                    // Draw Image inside clip
                    doc.addImage(userPhotoBase64, photoFormat, centerX - (photoW / 2), cursorY, photoW, photoH);
                    
                    // Restore context to stop clipping for rest of document
                    doc.context2d.restore(); 
                } catch (maskErr) {
                    console.error('Photo mask failed, falling back', maskErr);
                    // Fallback: draw image without mask
                    const photoFormat = userPhotoBase64.includes('image/png') ? 'PNG' : 'JPEG';
                    doc.addImage(userPhotoBase64, photoFormat, centerX - (photoW / 2), cursorY, photoW, photoH);
                }

                // Optional: Draw a thin border around the oval
                doc.setDrawColor(230, 230, 230);
                doc.setLineWidth(0.2);
                doc.ellipse(centerX, cursorY + (photoH / 2), photoW / 2, photoH / 2);
            } else {
                // Placeholder Circle
                doc.setFillColor(245, 245, 245);
                doc.setDrawColor(200, 200, 200);
                doc.ellipse(centerX, cursorY + (photoH / 2), photoW / 2, photoH / 2, 'FD');
            }

            cursorY += photoH + 4; // Move cursor past photo

            // --- 3. ID LABEL ---
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(TEXT_COLOR_ID);
            doc.text(`ID: ${associate.userAccess?.loginId || associate.loginId || '-'}`, centerX, cursorY, { align: 'center' });

            cursorY += 7; // Increased spacing between ID and Name

            // --- 4. NAME (Blue, Bold, Multi-line) ---
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13); // Slightly smaller than before to fit better
            doc.setTextColor(TEXT_COLOR_NAME);

            const nameStr = (associate.name || 'Name').toUpperCase();
            // Wrap text if too long
            const splitName = doc.splitTextToSize(nameStr, safeWidth);
            doc.text(splitName, centerX, cursorY, { align: 'center' });

            // Increase cursor based on how many lines name took (approx 5mm per line)
            cursorY += (splitName.length * 5) + 1;

            // --- 5. DESIGNATION STRIP ---
            const designationStr = `Desig. - ${associate.designation || 'ASSOCIATE'}`;

            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');

            // Measure designation text height
            const splitDesig = doc.splitTextToSize(designationStr, safeWidth);
            const stripHeight = (splitDesig.length * 4) + 2; // Dynamic height

            // Draw Strip Background
            doc.setFillColor(DESIGNATION_BG_COLOR);
            doc.rect(0, cursorY, cardWidth, stripHeight, 'F');

            // Draw Text (Centered vertically in strip)
            doc.setTextColor(TEXT_COLOR_MAIN);
            // +3 to push text down into the box
            doc.text(splitDesig, centerX, cursorY + 4, { align: 'center' });

            cursorY += stripHeight + 5; // Move cursor below strip

            // --- 6. DETAILS SECTION ---
            doc.setTextColor(TEXT_COLOR_MAIN);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);

            const addDetail = (label, value) => {
                if (!value) return;
                const text = `${label}: ${value}`;
                const splitText = doc.splitTextToSize(text, safeWidth);
                doc.text(splitText, centerX, cursorY, { align: 'center' });
                // Increment cursor dynamic based on lines
                cursorY += (splitText.length * 4.5);
            };

            addDetail('Registered Date', associate.joiningDate ? new Date(associate.joiningDate).toLocaleDateString() : '-');
            addDetail('Phone', associate.mobile || '-');
            addDetail('Email', associate.email || '-');

            // Address usually wraps, so it's good this is dynamic
            const addressVal = associate.city ? `${associate.city}, ${associate.state}` : associate.address;
            addDetail('Address', addressVal || 'Not Provided');

            // Save PDF
            doc.save(`${associate.name.replace(/\s+/g, '_')}_ID_Card.pdf`);

        } catch (err) {
            console.error('ID Card Error:', err);
            alert('Error generating ID Card');
        } finally {
            setGeneratingPdf(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon color="primary" /> Download ID Card
            </DialogTitle>

            <DialogContent dividers sx={{ textAlign: 'center' }}>
                {loadingData && (
                    <Box py={3}>
                        <CircularProgress size={30} />
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Fetching Details...
                        </Typography>
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {!loadingData && !error && companyData && associate && (
                    <Box py={2} px={1}>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {associate.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                            {associate.designation}
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={generateIDCard}
                            disabled={generatingPdf}
                            startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
                            sx={{
                                px: 5,
                                py: 1.2,
                                borderRadius: 50,
                                textTransform: 'none',
                                backgroundColor: TOP_BOTTOM_STRIP_COLOR,
                                '&:hover': { backgroundColor: '#1e7b9e' }
                            }}
                        >
                            {generatingPdf ? 'Processing...' : 'Download ID Card'}
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} startIcon={<CloseIcon />} color="inherit" disabled={generatingPdf}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PDFDownloadFunctionality;
